const MODAL_ID = "country-modal";
const ORBE_COOKIE = "mdApp_countryCodeDomain";
const ORBE_COOKIE_MAX_AGE = 60 * 60 * 24 * 60;

/**
 * Tells the Orbe geolocation app about a country chosen here. Without its
 * cookie, Orbe redirects a returning visitor back to the country it detected.
 * @see https://help.orbe.app/developer-reference/integrate-your-selector
 */
function rememberCountryForOrbe(countryCode, form) {
  // `typeof` rather than `window.orbito`: Orbe's global may be declared with
  // let/const, which never lands on window. Absent when the app is off.
  if (typeof orbito === "undefined") return;

  const domain = orbito.getCountryDomain();
  document.cookie = `${ORBE_COOKIE}=${countryCode}; domain=${domain}; path=/; max-age=${ORBE_COOKIE_MAX_AGE};`;

  const returnTo = form.querySelector('input[name="return_to"]');
  if (!returnTo) return;

  const url = new URL(returnTo.value, window.location.origin);
  url.searchParams.set(ORBE_COOKIE, countryCode);
  returnTo.value = url.pathname + url.search + url.hash;
}

class CountryModalTrigger extends HTMLElement {
  handleClick = () => {
    const template = document.getElementById("country-modal-template");

    this.button.dispatchEvent(
      new CustomEvent("markup-modal:open", {
        bubbles: true,
        detail: { target: MODAL_ID, html: template.innerHTML },
      }),
    );
  };

  connectedCallback() {
    this.button = this.querySelector("button");
    this.button.addEventListener("click", this.handleClick);
  }

  disconnectedCallback() {
    this.button.removeEventListener("click", this.handleClick);
  }
}

/**
 * The "Ship to" dropdown. `aria-listbox` handles the options — keyboard
 * movement, typeahead and selection — and this opens and closes it.
 */
class CountrySelect extends HTMLElement {
  handleButtonClick = () => {
    this._isOpen ? this._close() : this._open();
  };

  handleButtonKeyDown = (e) => {
    if (this._isOpen || !["ArrowDown", "ArrowUp"].includes(e.key)) return;

    e.preventDefault();
    // Otherwise the listbox, active by the time this reaches the document,
    // takes the same keypress and moves off the selected option.
    e.stopPropagation();
    this._open();
  };

  // Stopped here so the modal around the dropdown stays open.
  handleKeyUp = (e) => {
    if (e.key !== "Escape" || !this._isOpen) return;

    e.stopPropagation();
    this._close();
    this.button.focus();
  };

  handleFocusOut = (e) => {
    if (!e.relatedTarget || this.contains(e.relatedTarget)) return;

    this._close();
  };

  handleDocumentClick = (e) => {
    if (this.contains(e.target)) return;

    this._close();
  };

  handleChange = (e) => {
    this.value.textContent = e.target.textContent.trim();
    this._close();
    rememberCountryForOrbe(e.detail.value, this.form);
    this.form.submit();
  };

  connectedCallback() {
    this.form = this.querySelector("form");
    this.button = this.querySelector("button");
    this.value = this.querySelector("[data-value]");
    this.listbox = this.querySelector("aria-listbox");

    this.button.addEventListener("click", this.handleButtonClick);
    this.button.addEventListener("keydown", this.handleButtonKeyDown);
    this.addEventListener("keyup", this.handleKeyUp);
    this.addEventListener("focusout", this.handleFocusOut);
    this.addEventListener("aria-listbox:change", this.handleChange);
    document.addEventListener("click", this.handleDocumentClick);
  }

  disconnectedCallback() {
    document.removeEventListener("click", this.handleDocumentClick);
  }

  get _isOpen() {
    return !this.listbox.hidden;
  }

  _open() {
    this.listbox.hidden = false;
    this.button.setAttribute("aria-expanded", "true");
    // Moves focus to the selected option.
    this.listbox.setAttribute("active", "");
  }

  _close() {
    if (!this._isOpen) return;

    this.listbox.removeAttribute("active");
    this.listbox.hidden = true;
    this.button.setAttribute("aria-expanded", "false");
  }
}

if (!customElements.get("country-modal-trigger")) {
  customElements.define("country-modal-trigger", CountryModalTrigger);
}

if (!customElements.get("country-select")) {
  customElements.define("country-select", CountrySelect);
}
